import { Map, OrderedSet } from 'immutable';
import { QueryActionType } from '~/actions/query.actions';

import { NotebookTypes } from '@mode/notebooks/contract';
import { NotebookResource, SessionResource } from '@mode/legacy/data-access-model-js';
import { AppState, NormalizedNotebooksArray, NotebookState } from '@mode/legacy/types-app-state';
import { ReducerTree, ReportRunActionType } from '@mode/legacy/util-domain';
import { Actions } from '../../../actions/index';
import { CellActions } from '../../../actions/notebooks/cell.actions';
import { NotebookActions } from '../../../actions/notebooks/notebook.actions';
import { NotebookHelpers } from '../../../lib/notebooks';

export const initialNotebooksState: NormalizedNotebooksArray = {
  tokens: OrderedSet<string>(),
  pusherChannelNames: Map<string, string>(),
  sessions: Map<string, SessionResource>(),
  resources: Map<string, NotebookResource>(),
  initialized: Map<string, boolean>(),
  states: Map<string, NotebookState>(),
  awaitingInitialRun: Map<string, boolean>(),
  notebookTypes: Map<string, NotebookTypes.NotebookType>(),

  cellTokensByNotebook: Map<string, OrderedSet<string>>(),
};

function tokensReducer(state = initialNotebooksState.tokens, action: Actions): NormalizedNotebooksArray['tokens'] {
  switch (action.type) {
    case NotebookActions.Type.AddSuccess: // falls through
    case NotebookActions.Type.LoadSuccess: // falls through
    case NotebookActions.Type.LoadWithInitSuccess:
      return state.add(action.notebookResource.token);
    case NotebookActions.Type.Destroy:
      return state.remove(action.notebookToken);
  }

  return state;
}

function channelsReducer(
  state = initialNotebooksState.pusherChannelNames,
  action: Actions
): NormalizedNotebooksArray['pusherChannelNames'] {
  switch (action.type) {
    case NotebookActions.Type.UpdateSuccess: // falls through
    case NotebookActions.Type.AddSuccess: // falls through
    case NotebookActions.Type.LoadSuccess: // falls through
    case NotebookActions.Type.LoadWithInitSuccess:
      return state.set(action.notebookResource.token, action.notebookResource.pusher_channel_name);
    case NotebookActions.Type.Destroy:
      return state.remove(action.notebookToken);
    //Add notebook
  }

  return state;
}

function initializedReducer(
  state = initialNotebooksState.initialized,
  action: Actions
): NormalizedNotebooksArray['initialized'] {
  switch (action.type) {
    case NotebookActions.Type.LoadSuccess: // falls through
      return state.set(action.notebookResource.token, false);
    case NotebookActions.Type.AddSuccess: // falls through
    case NotebookActions.Type.LoadWithInitSuccess:
      return state.set(action.notebookResource.token, true);
    case NotebookActions.Type.InitComplete:
      const token = action.notebookToken;
      if (token) {
        return state.set(token, true);
      }
      break;
    case NotebookActions.Type.Destroy:
      return state.remove(action.notebookToken);
  }

  return state;
}

export function sessionsReducer(
  state = initialNotebooksState.sessions,
  action: Actions
): NormalizedNotebooksArray['sessions'] {
  switch (action.type) {
    case ReportRunActionType.RefreshReportRunAfterCellExport:
      /*
       *We need to update the report run last loaded run link to reflect the newly fetched report run in the state
       *We pick the latest_successful_report_run_api_url href from the fetched report run after cell export
       *And set the last_loaded_run link in the session state to this link, this ensures the notebook session does not get marked as outdated
       */
      const sessionResource = state.get(action.notebookToken) as any;
      const lastLoadedRunLink = action.reportRunResource.links('latest_successful_report_run_api_url').href;

      sessionResource.setLink('last_loaded_run', lastLoadedRunLink);
      return state.set(action.notebookToken, sessionResource);
    case NotebookActions.Type.LoadSessionSuccess:
      return state.set(action.notebookToken, action.sessionResource);
    case NotebookActions.Type.NoSession: // falls through
    case NotebookActions.Type.Destroy:
      return state.remove(action.notebookToken);
  }

  return state;
}

export function resourcesReducer(
  state = initialNotebooksState.resources,
  action: Actions
): NormalizedNotebooksArray['resources'] {
  switch (action.type) {
    case NotebookActions.Type.UpdateSuccess: // falls through
    case NotebookActions.Type.AddSuccess: // falls through
    case NotebookActions.Type.LoadSuccess: // falls through
    case NotebookActions.Type.LoadWithInitSuccess:
      return state.set(action.notebookResource.token, action.notebookResource);
    case NotebookActions.Type.Destroy:
      return state.remove(action.notebookToken);
    case NotebookActions.Type.SessionMetrics:
      // eslint-disable-next-line no-case-declarations
      const notebookResourceFromState = state.get(action.notebookToken);
      notebookResourceFromState.metrics = action.metrics;
      notebookResourceFromState.warn = NotebookHelpers.warnFromMetrics(action.metrics, notebookResourceFromState.warn);
      return state.set(notebookResourceFromState.token, notebookResourceFromState);
  }

  return state;
}

export function statesReducer(
  state = initialNotebooksState.states,
  action: Actions
): NormalizedNotebooksArray['states'] {
  switch (action.type) {
    case NotebookActions.Type.LoadSessionSuccess:
      if (action.sessionResource.active) {
        const isEmpty = NotebookHelpers.isSessionEmpty(action.sessionResource);
        if (!isEmpty) {
          const isOld = NotebookHelpers.isSessionReportRunOutdated(action.sessionResource, action.runToken);
          return state.set(action.notebookToken, isOld ? NotebookState.Alert : NotebookState.Ready);
        } else {
          // should attempt to load run right after
          return state.set(action.notebookToken, NotebookState.Loading);
        }
      } else {
        return state.set(action.notebookToken, NotebookState.Error);
      }
    case NotebookActions.Type.CancelRun:
      return state.set(action.notebookToken, NotebookState.Cancelling);
    case NotebookActions.Type.CancelRunSuccess:
      return state.set(action.notebookToken, NotebookState.Cancelled);
    case NotebookActions.Type.CancelRunComplete:
      return state.set(action.notebookToken, NotebookState.Ready);
    case NotebookActions.Type.NoSession: // falls through
    case NotebookActions.Type.RunFailed: // falls through
    case CellActions.Type.RunFailed:
      return state.set(action.notebookToken, NotebookState.Error);
    case NotebookActions.Type.SessionBusy:
      return state.set(action.notebookToken, NotebookState.Running);
    case NotebookActions.Type.InitSession:
      return state.set(action.notebookToken, NotebookState.Session);
    case NotebookActions.Type.Terminated:
      const notebookToken = action.notebookToken;
      if (notebookToken) {
        if (action.terminationReason !== 'OBO_USER') {
          return state.set(notebookToken, NotebookState.Error);
        }
        return state.set(notebookToken, NotebookState.Loading);
      }
      break;
    case NotebookActions.Type.InitSessionFailure:
      return state.set(action.notebookToken, NotebookState.Busy);
    case NotebookActions.Type.VerifySession: {
      const isOld = NotebookHelpers.isSessionReportRunOutdated(action.session, action.runToken);
      return state.map(() => (isOld ? NotebookState.Alert : NotebookState.Ready));
    }
    case NotebookActions.Type.RefreshSessionReportRun:
      return state.map(() => NotebookState.Loading);
    case NotebookActions.Type.DataLoaded:
      // TODO: When multiple notebooks support then this needs to be specific
      return state.map(() => NotebookState.Ready);
    case QueryActionType.DestroyQuery:
      return state.map(() => NotebookState.Alert);
  }
  return state;
}

function awaitingInitialRunReducer(
  state = initialNotebooksState.awaitingInitialRun,
  action: Actions
): NormalizedNotebooksArray['awaitingInitialRun'] {
  switch (action.type) {
    case NotebookActions.Type.InitSession:
      // Note that this will only attempt after AddSuccess, if the init fails, then the restart won't have runNewCells set
      return state.set(action.notebookToken, action.runNewCells === true);
    case NotebookActions.Type.InitialRunSuccess:
      return state.set(action.notebookToken, false);
    case NotebookActions.Type.Destroy:
      return state.remove(action.notebookToken);
  }

  return state;
}

function notebookTypesReducer(
  state = initialNotebooksState.notebookTypes,
  action: Actions
): NormalizedNotebooksArray['notebookTypes'] {
  switch (action.type) {
    case NotebookActions.Type.UpdateType:
      return state.set(action.notebookToken, action.notebookType);
    case NotebookActions.Type.UpdateTypeFailure: {
      return state.set(action.original.token, action.original.notebook_type);
    }
    case NotebookActions.Type.UpdateSuccess: // falls through
    case NotebookActions.Type.AddSuccess: // falls through
    case NotebookActions.Type.LoadSuccess: // falls through
    case NotebookActions.Type.LoadWithInitSuccess:
      return state.set(action.notebookResource.token, action.notebookResource.notebook_type);
    case NotebookActions.Type.Destroy:
      return state.remove(action.notebookToken);
  }

  return state;
}

function cellsByNotebookReducer(
  state = initialNotebooksState.cellTokensByNotebook,
  action: Actions
): NormalizedNotebooksArray['cellTokensByNotebook'] {
  switch (action.type) {
    case NotebookActions.Type.LoadSuccess: // falls through
    case NotebookActions.Type.LoadWithInitSuccess: // falls through
    case NotebookActions.Type.AddSuccess:
      return state.set(action.notebookResource.token, OrderedSet<string>());
    case NotebookActions.Type.Destroy:
      return state.remove(action.notebookToken);
    case CellActions.Type.LoadNotebookCellsSuccess:
      return state.set(action.notebookToken, OrderedSet(action.cellResources.map((cell) => cell.token)));
    case CellActions.Type.AddCellSuccess:
      if (action.nextToken) {
        const arr = state.get(action.notebookToken).toArray();
        arr.splice(arr.indexOf(action.nextToken), 0, action.cellResource.token);
        return state.set(action.notebookToken, OrderedSet(arr));
      }

      return state.set(action.notebookToken, state.get(action.notebookToken).add(action.cellResource.token));
    case CellActions.Type.DestroyCell:
      return state.set(action.notebookToken, state.get(action.notebookToken).remove(action.cellToken));
    case CellActions.Type.DestroyCellFailure:
      return state.set(action.notebookToken, state.get(action.notebookToken).add(action.cellToken));
    case CellActions.Type.MoveCell: {
      if (state.has(action.notebookToken)) {
        // doesn't hurt to be extra safe I guess
        const arr = state.get(action.notebookToken).toArray();
        const index = arr.indexOf(action.cellToken);
        if (index >= 0) {
          arr.splice(index, 1);
          if (action.down) {
            arr.splice(index + 1, 0, action.cellToken);
          } else {
            arr.splice(index - 1 >= 0 ? index - 1 : 0, 0, action.cellToken);
          }

          return state.set(action.notebookToken, OrderedSet(arr));
        }
      }
      break;
    }
    case CellActions.Type.MoveCellFailure: {
      const notebookToken = action.notebookToken;
      if (state.has(notebookToken)) {
        // doesn't hurt to be extra safe I guess
        const arr = state.get(notebookToken).toArray();
        const index = arr.indexOf(action.cellToken);

        if (index >= 0) {
          arr.splice(index, 1);
          const nextIndex = arr.indexOf(action.nextCellToken);
          arr.splice(nextIndex, 0, action.cellToken);
          return state.set(notebookToken, OrderedSet(arr));
        }
      }
      break;
    }
  }

  return state;
}
export const notebooksReducer: ReducerTree<AppState, NormalizedNotebooksArray, Actions> = {
  tokens: tokensReducer,
  pusherChannelNames: channelsReducer,
  initialized: initializedReducer,
  sessions: sessionsReducer,
  resources: resourcesReducer,
  states: statesReducer,
  awaitingInitialRun: awaitingInitialRunReducer,
  notebookTypes: notebookTypesReducer,

  cellTokensByNotebook: cellsByNotebookReducer,
};
